import { notification } from "antd";
import { v4 as uuidv4 } from "uuid";

export function formatDateAndTime(isoTimestamp: string): string {
  const date = new Date(isoTimestamp);

  // Extract month, day, and year
  const month = date.getMonth() + 1; // Month is 0-indexed
  const day = date.getDate();
  const yearShort = date.getFullYear().toString().slice(-2); // Get the last two digits of the year

  // Extract hour and minute
  let hour = date.getHours();
  const minute = date.getMinutes();

  // Determine AM/PM
  const ampm = hour >= 12 ? "PM" : "AM";

  // Convert 24-hour format to 12-hour format
  hour %= 12;
  hour = hour || 12; // Handle midnight (0 hour)

  // Format the output string
  const formattedDate = `${month}/${day}/${yearShort} at ${hour}:${minute
    .toString()
    .padStart(2, "0")} ${ampm}`;

  return formattedDate;
}

export function extractDateTime(timestamp: string): {
  date: string;
  time: string;
} {
  const dateObj = new Date(timestamp);

  if (Number.isNaN(dateObj.getTime())) {
    return { date: "Invalid Date", time: "Invalid Time" };
  }

  // Format date: YYYY/MM/DD
  const date = dateObj.toLocaleDateString("en-CA").replaceAll("-", "/");

  // Format time: h:mm AM/PM
  const time = dateObj.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  return { date, time };
}

export const openNotificationWithIcon = (
  type: "success" | "info" | "warning" | "error",
  message: string,
  description?: string
) => {
  notification[type]({
    message,
    description,
  });
};

export function getCookie(cookieName: "rfp_id" | "session_id" | "token"| "application_id") {
  const cookies = document.cookie.split(";");
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].trim();
    if (cookie.startsWith(`${cookieName}=`)) {
      return cookie.substring(cookieName.length + 1);
    }
  }
  return null;
}

export function getUserSector(): 'PUBLIC' | 'PRIVATE' | null {
  const localSector = localStorage.getItem('userSector');
  if (localSector === 'PUBLIC' || localSector === 'PRIVATE') {
    return localSector;
  }
  return null;
}

export const getColor = (name: string) => {
  const colors = [
    "#f56a00",
    "#7265e6",
    "#ffbf00",
    "#00a2ae",
    "#87d068",
  ];
  const index = name.charCodeAt(0) % colors.length;
  return colors[index];
};

export const getBase64FromUrl = async (url: string): Promise<string> => {
  const response = await fetch(url);
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

export function convertToCamelCase<T>(obj: T): T {
  if (Array.isArray(obj)) {
    return obj.map(convertToCamelCase) as T;
  } else if (obj !== null && typeof obj === "object") {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [
        key.replace(/_([a-z])/g, (_, char) => char.toUpperCase()), // Converts snake_case → camelCase
        convertToCamelCase(value),
      ])
    ) as T;
  }
  return obj;
}

export const generateUniqueId = (): string => {
  return uuidv4();
};

export function formatChatDate(date: Date): string {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const messageDate = new Date(date);
  messageDate.setHours(0, 0, 0, 0);
  
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  
  const diffTime = today.getTime() - messageDate.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) {
    return "Today";
  } else if (diffDays === 1) {
    return "Yesterday";
  } else {
    // Format as "Month Day, Year" (e.g., "January 15, 2024")
    return messageDate.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  }
}

export function formatChatTime(date: Date): string {
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

export function isSameDay(date1: Date, date2: Date): boolean {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}